import {
  AppCard,
  Button,
  FormTextField,
  Icon,
  RouteLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiEmailOutline, mdiEye, mdiEyeOff, mdiKey } from "@mdi/js";
import {
  Box,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { useCallback, useMemo, useRef, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getCookie } from "typescript-cookie";
import { EquinorLogo } from "../../../../components/atoms/EquinorLogo/EquinorLogo";
import {
  Recaptcha,
  type RecaptchaRef,
} from "../../../../components/atoms/Recaptcha/Recaptcha";
import { FullScreenLoading } from "../../../../components/molecules/FullScreenLoading";
import { config } from "../../../../config";
import { useAuth } from "../../../../hooks/useAuth";
import { type formData, loginSchema } from "./-schemas/loginSchema";

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

// biome-ignore lint/correctness/noUnusedVariables: TODO: Change to the following line when all routes are migrated to React
const fallback = "/" as const;

const SignIn: React.FC = () => {
  const search = Route.useSearch();

  const navigate = Route.useNavigate();

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { signIn, isAuthenticated } = useAuth();
  const recaptchaRef = useRef<RecaptchaRef>(null);
  const router = useRouter();

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const redirectUri = useMemo(() => search.redirect_uri, [search]);

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      password: "",
      username: "",
    },
    resolver: zodResolver(loginSchema),
  });

  const buildSSOUrl = useCallback((): string => {
    const baseUrl = config.auth.omniauthAzureUrl;
    if (redirectUri) {
      return `${baseUrl}?origin=${encodeURIComponent(redirectUri)}`;
    }
    return baseUrl;
  }, [redirectUri]);

  const handleSSOLogin = useCallback(() => {
    setLoading(() => true);

    // Clear sessions values that need to be reset in each sign in
    sessionStorage.removeItem("announcementsModalViewed");
  }, []);

  const onSubmit: SubmitHandler<formData> = useCallback(
    async (values) => {
      const token = recaptchaRef.current?.getValue();

      if (
        !token &&
        !(window.environment === "test" || window.environment === "development")
      ) {
        enqueueSnackbar(t("auth.signIn.invalidBotVerificationMessage"));
        return;
      }

      setLoading(() => true);

      if (values.username && values.password) {
        const { username, password } = values;
        try {
          await signIn({
            email: username,
            password,
            token: token ?? "",
          });

          await router.invalidate();

          // This is just a hack being used to wait for the auth state to update
          // in a real app, you'd want to use a more robust solution
          await sleep(1);

          // TODO: Change to the following line when all routes are migrated to React
          // await navigate({ to: search.redirect_uri || fallback, replace: true });

          await navigate({
            replace: true,
            search: { redirect_uri: search.redirect_uri },
            to: "/redirect/rails/home",
          });
        } catch {
          recaptchaRef.current?.reset();
          setLoading(() => false);
        }
      }
    },
    [enqueueSnackbar, navigate, router, search.redirect_uri, signIn, t],
  );

  if (isAuthenticated) {
    return <FullScreenLoading />;
  }

  return (
    <Container
      maxWidth="xs"
      sx={{
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        minHeight: "100vh",
        padding: "0px !important",
      }}
    >
      <AppCard slotProps={{ root: { sx: { padding: 3 } } }}>
        <EquinorLogo />
        <form
          action={buildSSOUrl()}
          method="post"
          name="loginEquinorEmployee"
          onSubmit={() => handleSSOLogin()}
        >
          <input name="_method" type="hidden" value="post" />
          <input
            name="authenticity_token"
            type="hidden"
            value={getCookie(config.csrfTokenCookieName)}
          />
          <Button
            color="energyRed"
            disabled={window.environment === "development"}
            fullWidth
            loading={loading}
            loadingPosition="center"
            sx={{ mb: 1, mt: 1 }}
            type="submit"
            variant="contained"
          >
            {t("auth.signIn.equinorEmployeeButton")}
          </Button>
        </form>

        <Divider
          sx={{
            alignItems: "center",
            display: "flex",
            margin: "24px 0 16px 0",
          }}
        >
          <Typography
            sx={{
              color: "text.secondary",
              fontWeight: "bold",
            }}
            variant="body1"
          >
            {t("common.or").toUpperCase()}
          </Typography>
        </Divider>

        <form name="loginExternalUser" onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormTextField
              aria-required="true"
              autoComplete="username"
              autoFocus
              control={control}
              fullWidth
              margin="normal"
              name="username"
              onChange={(event) => {
                const normalizedValue = event.target.value.trim();
                if (event.target.value !== normalizedValue) {
                  setValue("username", normalizedValue, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }
              }}
              placeholder={t("common.email")}
              slotProps={{
                input: {
                  startAdornment: (
                    <Icon
                      icon={mdiEmailOutline}
                      sx={{
                        color: (currentTheme) =>
                          currentTheme.vars.palette.action.active,
                        marginRight: 1,
                      }}
                    />
                  ),
                },
              }}
              type="email"
            />

            <FormTextField
              aria-required="true"
              autoComplete="current-password"
              control={control}
              fullWidth
              name="password"
              onChange={(event) => {
                const normalizedValue = event.target.value.trim();
                if (event.target.value !== normalizedValue) {
                  setValue("password", normalizedValue, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }
              }}
              placeholder={t("common.password")}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t(
                          "auth.signIn.togglePasswordVisibilityLabel",
                        )}
                        edge="end"
                        onMouseDown={() => setShowPassword(() => true)}
                        onMouseOutCapture={() => setShowPassword(() => false)}
                        onMouseUp={() => setShowPassword(() => false)}
                      >
                        {showPassword ? (
                          <Icon icon={mdiEye} />
                        ) : (
                          <Icon icon={mdiEyeOff} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                  startAdornment: (
                    <Icon
                      icon={mdiKey}
                      sx={{
                        color: (currentTheme) =>
                          currentTheme.vars.palette.action.active,
                        marginRight: 1,
                      }}
                    />
                  ),
                },
              }}
              sx={{ mb: 1 }}
              type={showPassword ? "text" : "password"}
            />

            <RouteLink to="/users/password/new">
              {t("auth.signIn.forgotPasswordLink")}
            </RouteLink>

            <Recaptcha ref={recaptchaRef} />

            <Button
              fullWidth
              loading={loading}
              loadingPosition="center"
              sx={{ mb: 1, mt: 1 }}
              type="submit"
              variant="contained"
            >
              {t("auth.signIn.externalUserButton")}
            </Button>
          </Box>
        </form>
        <Divider sx={{ margin: "8px 0px" }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            justifyContent: "space-between",
          }}
        >
          <RouteLink to="/users/sign_up">{t("common.createAccount")}</RouteLink>
          <RouteLink to="/users/confirmation/new">
            {t("auth.signIn.resendConfirmationInstructionsLink")}
          </RouteLink>
        </Box>
      </AppCard>
    </Container>
  );
};

export const Route = createFileRoute("/_public/_auth/users/sign_in")({
  component: SignIn,
});
